import * as d3 from 'd3';
import { assertSuccess, get } from './request';
import { BACKEND_URL, requestOptions } from './s4p';
import {
  StreamsByRegion,
  StreamsByRegionRaw,
  TimelineAnnotation,
  TimelineAnnotationsResponse,
} from './s4pTypes';

export const TRACKS_PER_TIMELINE_REQUEST = 5000;
export const parseTime = d3.timeParse('%Y-%m-%d');

type StreamsByRegionDate = {
  [region: string]: {
    [date: string]: number;
  };
};

const updateAggregate = (aggregate: StreamsByRegionDate, newData: StreamsByRegionRaw) => {
  for (const [country, dataPoints] of Object.entries(newData)) {
    if (!(country in aggregate)) {
      aggregate[country] = {};
    }

    for (const { date, value } of dataPoints) {
      const existing = aggregate[country][date] || 0;
      aggregate[country][date] = existing + value;
    }
  }
};

const convertToStreamsByRegion = (streamsByRegionDate: StreamsByRegionDate): StreamsByRegion => {
  return Object.entries(streamsByRegionDate).reduce<StreamsByRegion>((parsed, [country, dates]) => {
    parsed[country] = Object.entries(dates).map(([date, value]) => ({
      date: parseTime(date) as Date,
      value: value,
    }));

    return parsed;
  }, {});
};

const getTimelineStreamsPage = async (
  baseRoute: string,
  startDate: string,
  endDate: string,
  page: number,
  onErrorCallback?: Function,
): Promise<{ data: StreamsByRegionRaw; totalPages: number }> => {
  const parameters = `start-date=${startDate}&end-date=${endDate}&page=${page}&results-per-page=${TRACKS_PER_TIMELINE_REQUEST}`;
  const response = await get(`${BACKEND_URL}${baseRoute}?${parameters}`, requestOptions);

  if (response.status !== 200 && onErrorCallback) {
    onErrorCallback();
  }

  assertSuccess(response);

  return await response.json();
};

const recursivelyGetTimelineStreams = (
  baseRoute: string,
  startDate: string,
  endDate: string,
  page: number,
  aggregate: StreamsByRegionDate,
  onErrorCallback?: Function,
): Promise<StreamsByRegion> => {
  return getTimelineStreamsPage(baseRoute, startDate, endDate, page, onErrorCallback).then(
    ({ data, totalPages }) => {
      updateAggregate(aggregate, data);
      if (totalPages && page < totalPages) {
        return recursivelyGetTimelineStreams(
          baseRoute,
          startDate,
          endDate,
          page + 1,
          aggregate,
          onErrorCallback,
        );
      } else {
        return convertToStreamsByRegion(aggregate);
      }
    },
  );
};

export const getTimelineStreams = (
  baseRoute: string,
  startDate: string,
  endDate: string,
  onErrorCallback?: Function,
) => {
  return recursivelyGetTimelineStreams(baseRoute, startDate, endDate, 1, {}, onErrorCallback);
};

export function parseTimelineAnnotations(
  response: TimelineAnnotationsResponse,
): TimelineAnnotation[] {
  return response.annotations_by_date.map((annotation) => ({
    date: parseTime(annotation.date) as Date,
    type: annotation.annotation_type_id,
    metadata: annotation.annotation_metadata.map((meta) => ({
      name: meta.annotation_name,
      id: meta.annotation_id,
    })),
  }));
}
